<template>
  <section>
    <form @submit.prevent="submit">
      <Field
        label="Full Name"
        :placeholder="namePlaceholder"
        @input="$v.resource.name.$touch()"
        v-model="resource.name"
        :error="nameError"
      />

      <Field
        v-if="notEditingSelf"
        label="Email Address"
        @input="$v.resource.email.$touch()"
        v-model="resource.email"
        :error="emailError"
      />

      <hr>
    </form>

    <div v-if="notEditingSelf">
      <h2>User Permissions</h2>

      <div class="set-permissions">
        <Btn
          :class="{'is-active': isActiveRole('Editor')}"
          class="editor-role-btn"
          subtle
          @click="setUserPermissions('Editor')">
          Editor
        </Btn>

        <Btn
          :class="{'is-active': isActiveRole('Limited')}"
          subtle
          @click="setUserPermissions('Limited')">
          Limited
        </Btn>
      </div>

      <div>
        <p v-html="roleContent"></p>
        <p>Only the company owner can view and manage <strong>billing</strong> and <strong>domain names</strong>. If you would like to transfer ownership to another team member, please contact our team.</p>
      </div>

      <transition name="fade" mode="out-in">
        <div key="permissions" class="permissions">
          <div class="inner-scroll">
            <fieldset
              class="all-permissions"
              :class="`is-${relm}`"
              v-for="(actions, relm, index) in availablePermissions"
              :key="`${relm}-${index}`"
            >
              <div class="permission-heading">
                <Sprite :name="relm" />
                <h3>{{ humanize(relm) }}</h3>
              </div>

              <div
                class="relm-wrapper"
                v-for="(action, index) in actions"
                :key="`${action}-${index}`"
              >
                <input
                  :id="`${action}-${relm}`"
                  type="checkbox"
                  class="action-checkbox"
                  v-model="resource.permissions[relm]"
                  :value="action"
                  @input="$v.resource.permissions.$touch"
                />

                <label :for="`${action}-${relm}`">{{ action }} {{ relm }}</label>
             </div>
            </fieldset>
          </div>
        </div>
      </transition>

      <error :error="permissionsError" />

      <hr />
    </div>

    <div class="btn-save">
      <Btn @click="submit" primary>
        <span v-if="addUser">Send Invite via email</span>
        <span v-else>Save Changes</span>
      </Btn>
    </div>
  </section>
</template>

<script>
import form from '@/mixins/form'
import celebrityNames from '@/mixins/celebrityNames'

import Btn from '@/components/Btn'
import Error from '@/components/Error'
import Sprite from '@/components/Sprite'

import {mapState} from 'vuex'

import {errorMessages} from '@/lib/error_messages'
import {required, email} from 'vuelidate/lib/validators'

export default {
  name: 'CompanyUserForm',

  mixins: [form, celebrityNames],

  data() {
    return {
      activeRole: '',
      contentLimited: `The user will be able to <strong>view content</strong> such as websites and team members. They will <strong>not</strong> have the ability to make any content changes whatsoever.`,
      contentEditor: `The user will be able to <strong>edit any content</strong> on any website within the company. They will <strong>not</strong> have the ability to make any changes to team members.`
    }
  },

  props: {
    availablePermissions: {
      type: Object,
      default() {
        return {}
      }
    }
  },

  components: {
    Btn,
    Error,
    Sprite
  },

  computed: {
    ...errorMessages(['name', 'email', 'password', 'permissions']),
    ...mapState(['currentUser']),

    notEditingSelf() {
      if (!this.addUser) {
        return this.resource.id !== this.currentUser.id
      }

      return true
    },

    humanize() {
      return (relm) => {
        return relm.replace('_', ' ')
      }
    },

    addUser() {
      return this.$route.name === 'add-company-user'
    },

    isActiveRole() {
      return (role) => {
        return this.activeRole === role
      }
    },

    roleContent() {
      return this['content' + this.activeRole]
    }
  },

  validations: {
    resource: {
      name: {required},
      email: {required, email},
      permissions: {}
    }
  },

  methods: {
    setUserPermissions(role) {
      this.activeRole = role
      this.$emit('setUserPermissions', role)
    }
  },

  mounted() {
    this.setUserPermissions('Editor')
  }
}
</script>

<style lang="sass">
@import '../../stylesheets/base/variables'
@import '../../stylesheets/helpers/mixins'
@import '../../stylesheets/helpers/extends'

.permissions
  @extend %permissions

.all-permissions
  &.is-default
    display: none

.set-permissions
  margin: $gap-medium 0
  padding: $gap-small
  border-radius: 5px
  background: rgba(0, 0, 0, 0.025)
  display: flex
  justify-content: center

  .builder-tip
    width: 175px!important
    transform: translate(-50%, -40px)!important

  .btn
    margin-right: $gap-small

  .editor-role-btn
    margin-right: $gap-medium
    position: relative

  .is-active
    .btn
      @include themify($themes)
        background: themed('color-secondary')

</style>
